/**
 * AI Coach + check-in hint/assessment service.
 */

import apiClient, { getCsrfToken, getApiBaseUrl } from './api';
import type { ApiResponse } from '../types';

export interface CoachQualityBlock {
  score: number;
  label?: string;
  criteria: Array<{ name: string; status: 'pass' | 'warn' | 'fail' }>;
  note: string;
}

export interface CoachActionBlock {
  findings: Array<{ type: 'good' | 'warn' | 'info' | 'bad'; text: string }>;
}

export interface CoachPerformanceBlock {
  score: number | null;
  trend: number | null;
  note: string;
}

export interface CoachOrgContextBlock {
  text: string;
}

export interface CoachInsightView {
  qualityBlock: CoachQualityBlock;
  actionBlock: CoachActionBlock;
  performanceBlock: CoachPerformanceBlock;
  orgContextBlock: CoachOrgContextBlock;
  status: 'ready' | 'generating' | 'failed' | 'stale';
  generatedAt: string | null;
  isRefreshing: boolean;
  errorMessage: string | null;
  tokensUsed: number | null;
}

export interface CheckInAssessment {
  quality: 'good' | 'fair' | 'poor';
  summary: string;
  flags: string[];
  generatedAt: string;
  tokensUsed?: number;
}

export const aiCoachService = {
  getCoach: async (okrId: string, opts?: { force?: boolean }): Promise<CoachInsightView> => {
    const r = await apiClient.get<ApiResponse<{ coach: CoachInsightView }>>(
      `/ai/okrs/${okrId}/coach`,
      { params: opts?.force ? { refresh: 'true' } : undefined }
    );
    if (r.data.success && r.data.data) return r.data.data.coach;
    throw new Error(r.data.message || 'Failed to load AI Coach');
  },

  getDraftHint: async (input: {
    okrId: string;
    field: 'accomplishments' | 'next_steps';
    text: string;
  }): Promise<{ hint: string | null }> => {
    const r = await apiClient.post<ApiResponse<{ hint: string | null }>>(
      '/ai/checkins/draft/hint',
      input
    );
    if (r.data.success && r.data.data) return r.data.data;
    throw new Error(r.data.message || 'Failed to get hint');
  },

  summarizeCheckIn: async (input: {
    okrId: string;
    notes: Array<{
      keyResultId: string;
      accomplishments?: string;
      challenges?: string;
      nextSteps?: string;
    }>;
  }): Promise<{ summary: string | null }> => {
    const r = await apiClient.post<ApiResponse<{ summary: string | null }>>(
      '/ai/checkins/draft/summary',
      input
    );
    if (r.data.success && r.data.data) return r.data.data;
    throw new Error(r.data.message || 'Failed to summarize');
  },

  assessCheckIn: async (checkInId: string): Promise<CheckInAssessment> => {
    const r = await apiClient.post<ApiResponse<{ assessment: CheckInAssessment }>>(
      `/ai/checkins/${checkInId}/assess`
    );
    if (r.data.success && r.data.data) return r.data.data.assessment;
    throw new Error(r.data.message || 'Failed to assess check-in');
  },
};

// ─── Chat ("Ask about this OKR") ─────────────────────────────────────────────

export interface AICoachMessageView {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  tokensUsed: number | null;
  model: string | null;
}

export interface AskCoachResponse {
  userMessage: AICoachMessageView;
  assistantMessage: AICoachMessageView;
}

export interface AskStreamError {
  code: string;
  message: string;
}

export interface AskStreamHandlers {
  onUserSaved: (m: AICoachMessageView) => void;
  onDelta: (text: string) => void;
  onDone: (m: AICoachMessageView) => void;
  onError: (e: AskStreamError) => void;
  signal?: AbortSignal;
}

export const aiCoachChat = {
  listMessages: async (okrId: string): Promise<AICoachMessageView[]> => {
    const r = await apiClient.get<ApiResponse<{ messages: AICoachMessageView[] }>>(
      `/ai/okrs/${okrId}/messages`
    );
    if (r.data.success && r.data.data) return r.data.data.messages;
    throw new Error(r.data.message || 'Failed to load conversation');
  },

  askQuestion: async (okrId: string, question: string): Promise<AskCoachResponse> => {
    const r = await apiClient.post<ApiResponse<AskCoachResponse>>(
      `/ai/okrs/${okrId}/ask`,
      { question }
    );
    if (r.data.success && r.data.data) return r.data.data;
    throw new Error(r.data.message || 'Failed to ask coach');
  },

  /**
   * Streaming variant: POSTs to /ask-stream and pipes the SSE events to the
   * supplied handlers. Resolves once the server emits `done`; rejects (after
   * firing onError) on any error event, abort, network failure, or premature
   * EOF. Uses fetch + ReadableStream rather than EventSource because
   * EventSource cannot carry custom headers (CSRF + auth cookies are still
   * needed even with `credentials: 'include'`).
   */
  askQuestionStream: async (
    okrId: string,
    question: string,
    handlers: AskStreamHandlers
  ): Promise<void> => {
    let csrf = '';
    try {
      csrf = await getCsrfToken();
    } catch (err) {
      const e: AskStreamError = { code: 'CSRF_ERROR', message: 'CSRF token unavailable' };
      handlers.onError(e);
      throw Object.assign(new Error(e.message), { code: e.code });
    }

    let res: Response;
    try {
      res = await fetch(`${getApiBaseUrl()}/ai/okrs/${okrId}/ask-stream`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf,
        },
        body: JSON.stringify({ question }),
        signal: handlers.signal,
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      const e: AskStreamError = {
        code: 'NETWORK_ERROR',
        message: (err as Error).message || 'Network error',
      };
      handlers.onError(e);
      throw Object.assign(new Error(e.message), { code: e.code });
    }

    if (!res.ok) {
      let code = 'HTTP_ERROR';
      let message = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        code = body.code || code;
        message = body.message || message;
      } catch {
        /* body wasn't JSON — keep the defaults */
      }
      const e: AskStreamError = { code, message };
      handlers.onError(e);
      throw Object.assign(new Error(message), { code });
    }

    const reader = res.body?.getReader();
    if (!reader) {
      const e: AskStreamError = {
        code: 'STREAM_UNSUPPORTED',
        message: 'Response body is not readable',
      };
      handlers.onError(e);
      throw Object.assign(new Error(e.message), { code: e.code });
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let streamError: AskStreamError | null = null;
    let doneSeen = false;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const dataLine = block.split('\n').find((l) => l.startsWith('data: '));
          if (!dataLine) continue;
          const payload = dataLine.slice(6);
          try {
            const event = JSON.parse(payload) as
              | { type: 'user-saved'; userMessage: AICoachMessageView }
              | { type: 'delta'; text: string }
              | { type: 'done'; assistantMessage: AICoachMessageView }
              | { type: 'error'; code: string; message: string };
            switch (event.type) {
              case 'user-saved':
                handlers.onUserSaved(event.userMessage);
                break;
              case 'delta':
                handlers.onDelta(event.text);
                break;
              case 'done':
                doneSeen = true;
                handlers.onDone(event.assistantMessage);
                break;
              case 'error':
                streamError = { code: event.code, message: event.message };
                break;
            }
          } catch {
            /* malformed SSE payload — skip */
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      const e: AskStreamError = {
        code: 'STREAM_ERROR',
        message: (err as Error).message || 'Stream interrupted',
      };
      handlers.onError(e);
      throw Object.assign(new Error(e.message), { code: e.code });
    }

    if (streamError) {
      handlers.onError(streamError);
      throw Object.assign(new Error(streamError.message), { code: streamError.code });
    }
    if (!doneSeen) {
      const e: AskStreamError = {
        code: 'STREAM_INCOMPLETE',
        message: 'Stream ended without a done event',
      };
      handlers.onError(e);
      throw Object.assign(new Error(e.message), { code: e.code });
    }
  },

  clearMessages: async (okrId: string): Promise<number> => {
    const r = await apiClient.delete<ApiResponse<{ deleted: number }>>(
      `/ai/okrs/${okrId}/messages`
    );
    if (r.data.success && r.data.data) return r.data.data.deleted;
    throw new Error(r.data.message || 'Failed to clear conversation');
  },
};
